import React, { useCallback, useEffect, useState } from 'react';
import SeatManager from '../components/admin/SeatManager';
import { AdminGate, adminFetch } from '../components/admin/AdminGate';
import { MessageSquare, Mail, Users, ArrowLeft, Send, Loader2, RefreshCw, Sparkles, Download } from 'lucide-react';


const fmt = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

function ChatDashboard() {
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [handoffs, setHandoffs] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [detail, setDetail] = useState(null); // {session_id, messages}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [digestSending, setDigestSending] = useState(false);
  const [digestResult, setDigestResult] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [ov, ses, ho, subs] = await Promise.all([
        adminFetch('/admin/overview').then((r) => r.ok ? r.json() : Promise.reject(r.status)),
        adminFetch('/admin/sessions?limit=25').then((r) => r.ok ? r.json() : Promise.reject(r.status)),
        adminFetch('/admin/handoffs?limit=25').then((r) => r.ok ? r.json() : Promise.reject(r.status)),
        adminFetch('/admin/subscribers?limit=200').then((r) => r.ok ? r.json() : Promise.reject(r.status)),
      ]);
      setOverview(ov.totals || null);
      setSessions(ses.sessions || []);
      setHandoffs(ho.handoffs || []);
      setSubscribers(subs.subscribers || []);
    } catch (err) {
      if (err === 401) setError('Session expired. Please sign in again.');
      else setError(String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openSession = async (sid) => {
    setDetail({ session_id: sid, messages: null });
    try {
      const resp = await adminFetch(`/admin/sessions/${sid}`);
      if (!resp.ok) throw new Error(`Failed (${resp.status})`);
      const data = await resp.json();
      setDetail({ session_id: sid, messages: data.messages || [] });
    } catch (err) {
      setDetail({ session_id: sid, messages: [], error: String(err) });
    }
  };

  const sendDigest = async () => {
    setDigestSending(true); setDigestResult('');
    try {
      const resp = await adminFetch('/admin/digest/send', { method: 'POST' });
      if (!resp.ok) throw new Error(`Failed (${resp.status})`);
      const data = await resp.json();
      setDigestResult(data.sent ? 'Digest email sent!' : `Failed: ${data.err || 'unknown error'}`);
    } catch (err) {
      setDigestResult(String(err));
    } finally {
      setDigestSending(false);
    }
  };

  return (
    <div data-testid="admin-dashboard">
      <div>
        <div className="flex items-center justify-between gap-4 mb-6">
          <h1 className="font-display font-bold text-2xl md:text-3xl text-splash-green">Chat Admin</h1>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-splash-sand text-splash-green px-3 py-1.5 text-sm font-semibold btn-transition hover:bg-splash-cream" data-testid="admin-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        {error && <div className="mb-4 rounded-xl bg-white border border-splash-coral/30 text-splash-coralDark px-4 py-3 text-sm">{error}</div>}

        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="admin-overview">
            {[
              { label: '7-Day Chat Msgs', v: overview.chat_messages_last_7d, icon: MessageSquare },
              { label: '7-Day Handoffs', v: overview.handoffs_last_7d, icon: Mail },
              { label: '7-Day Enrollments', v: overview.enrollments_last_7d, icon: Users },
              { label: '7-Day Waitlist', v: overview.waitlist_last_7d, icon: Users },
              { label: '7-Day Subscribers', v: overview.subscribers_last_7d, icon: Sparkles },
            ].map(({ label, v, icon: I }) => (
              <div key={label} className="rounded-2xl bg-white border border-splash-sand p-4">
                <I className="w-4 h-4 text-splash-teal mb-1" />
                <div className="font-display font-bold text-2xl text-splash-green">{v}</div>
                <div className="text-[10px] uppercase tracking-widest text-splash-green/60">{label}</div>
              </div>
            ))}
          </div>
        )}

        <SeatManager />

        <div className="rounded-2xl bg-white border border-splash-sand p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-splash-coral font-semibold">Weekly digest</div>
            <div className="text-sm text-splash-green/80">Auto-runs every Monday 6 AM Central. Preview or force send now.</div>
            {digestResult && <div className="text-xs text-splash-green/70 mt-1" data-testid="admin-digest-result">{digestResult}</div>}
          </div>
          <button onClick={sendDigest} disabled={digestSending} data-testid="admin-digest-send" className="inline-flex items-center gap-2 rounded-full bg-splash-teal hover:bg-splash-tealDark disabled:opacity-60 text-white px-4 py-2 text-sm font-semibold btn-transition self-start sm:self-auto">
            {digestSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send Digest Now
          </button>
        </div>

        {detail ? (
          <div className="rounded-2xl bg-white border border-splash-sand p-4" data-testid="admin-session-detail">
            <button onClick={() => setDetail(null)} className="inline-flex items-center gap-1 text-splash-teal text-sm font-semibold mb-3">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sessions
            </button>
            <div className="text-xs text-splash-green/60 mb-3">Session: {detail.session_id}</div>
            {detail.messages === null && <div className="text-sm text-splash-green/60">Loading…</div>}
            {detail.messages && detail.messages.length === 0 && <div className="text-sm text-splash-green/60">No messages.</div>}
            <div className="space-y-2">
              {(detail.messages || []).map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] whitespace-pre-wrap text-sm leading-relaxed px-3 py-2 rounded-2xl ${
                    m.role === 'user' ? 'bg-splash-teal text-white' : 'bg-splash-cream text-splash-green border border-splash-sand'
                  }`}>
                    <div className="text-[10px] uppercase tracking-widest opacity-70 mb-0.5">{m.role} · {fmt(m.created_at)}</div>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            <section className="rounded-2xl bg-white border border-splash-sand p-4">
              <h2 className="font-display font-bold text-lg text-splash-green mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Recent chat sessions</h2>
              {sessions.length === 0 && <div className="text-sm text-splash-green/60">No sessions yet.</div>}
              <ul className="space-y-2" data-testid="admin-sessions-list">
                {sessions.map((s) => (
                  <li key={s.session_id}>
                    <button onClick={() => openSession(s.session_id)} className="w-full text-left rounded-xl border border-splash-sand hover:bg-splash-cream px-3 py-2 btn-transition">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-xs text-splash-green/60 truncate">{s.session_id}</div>
                        <div className="text-[10px] text-splash-green/50">{s.message_count} msgs · {fmt(s.last_at)}</div>
                      </div>
                      <div className="text-sm text-splash-green mt-1 line-clamp-2">{s.preview || <em className="text-splash-green/50">No parent message yet</em>}</div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-2xl bg-white border border-splash-sand p-4">
              <h2 className="font-display font-bold text-lg text-splash-green mb-3 flex items-center gap-2"><Mail className="w-4 h-4" /> Handoffs waiting</h2>
              {handoffs.length === 0 && <div className="text-sm text-splash-green/60">No handoffs yet.</div>}
              <ul className="space-y-2" data-testid="admin-handoffs-list">
                {handoffs.map((h) => (
                  <li key={h.id} className="rounded-xl border border-splash-sand px-3 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm text-splash-green font-semibold">{h.parent_name}</div>
                      <div className="text-[10px] text-splash-green/50">{fmt(h.created_at)}</div>
                    </div>
                    <div className="text-xs text-splash-teal">
                      <a href={`mailto:${h.parent_email}`}>{h.parent_email}</a>
                    </div>
                    <div className="text-sm text-splash-green/85 mt-1 whitespace-pre-wrap">{h.question}</div>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}

        {!detail && (
          <section className="mt-6 rounded-2xl bg-white border border-splash-sand p-4" data-testid="admin-subscribers-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h2 className="font-display font-bold text-lg text-splash-green flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-splash-coral" /> Email subscribers
                <span className="text-xs font-normal text-splash-green/60">({subscribers.length})</span>
              </h2>
              <button
                type="button"
                onClick={() => {
                  const header = 'email,first_name,promo_code,referral_code,referral_count,referred_by_code,source,created_at\n';
                  const rows = subscribers.map((s) => [s.email, s.first_name || '', s.promo_code || '', s.referral_code || '', s.referral_count || 0, s.referred_by_code || '', s.source || '', s.created_at || ''].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
                  const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                }}
                disabled={subscribers.length === 0}
                data-testid="admin-subscribers-export"
                className="inline-flex items-center gap-1.5 rounded-full bg-splash-teal hover:bg-splash-tealDark disabled:opacity-60 text-white px-3 py-1.5 text-xs font-semibold btn-transition"
              >
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>
            {subscribers.length === 0 ? (
              <div className="text-sm text-splash-green/60">No subscribers yet.</div>
            ) : (
              <div className="overflow-x-auto -mx-4 px-4">
                <table className="w-full text-sm" data-testid="admin-subscribers-list">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-splash-green/60">
                      <th className="py-2 pr-3 font-semibold">Name</th>
                      <th className="py-2 pr-3 font-semibold">Email</th>
                      <th className="py-2 pr-3 font-semibold">Promo Code</th>
                      <th className="py-2 pr-3 font-semibold">Referrals</th>
                      <th className="py-2 pr-3 font-semibold">Source</th>
                      <th className="py-2 pr-3 font-semibold">Signed Up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((s) => (
                      <tr key={s.id} className="border-t border-splash-sand/60">
                        <td className="py-2 pr-3 text-splash-green">{s.first_name || <span className="text-splash-green/40">—</span>}</td>
                        <td className="py-2 pr-3 text-splash-teal"><a href={`mailto:${s.email}`}>{s.email}</a></td>
                        <td className="py-2 pr-3 font-mono text-xs text-splash-green">{s.promo_code}</td>
                        <td className="py-2 pr-3">
                          {(s.referral_count || 0) > 0 ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-splash-coral/15 text-splash-coralDark px-2 py-0.5 text-xs font-semibold">
                              <Sparkles className="w-3 h-3" /> {s.referral_count}
                            </span>
                          ) : (
                            <span className="text-xs text-splash-green/40">0</span>
                          )}
                          {s.referred_by_code && (
                            <div className="text-[10px] text-splash-green/50 mt-0.5">via {s.referred_by_code}</div>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-splash-green/70 text-xs">{s.source}</td>
                        <td className="py-2 pr-3 text-splash-green/60 text-xs whitespace-nowrap">{fmt(s.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default function AdminChats() {
  return <AdminGate title="Admin Dashboard"><ChatDashboard /></AdminGate>;
}
